import { useEffect, useMemo, useState } from 'react';
import { Popover } from '~/components/ui/popover';
import { useApprovals } from '~/hooks/queries/use-approvals';
import { useProBuilderEnabled } from '~/hooks/queries/use-feature-flags';
import { useCodingEnabled } from '~/hooks/queries/use-tenant';
import { useProBuilderOpenStore } from '~/lib/stores/pro-builder-open-store';
import { useAttentionStore } from '~/lib/stores/attention-store';
import { ActivityBoard } from './activity-board';
import { AskChip } from './ask-chip';
import { BuildButton } from './build-button';
import { NewChatButton } from './new-chat-button';
import { ReviewCount, StatusCluster } from './status-cluster';
import { firstAsking, resolveAskChip } from './state-mapping';
import { useChatActivity } from './use-chat-activity';
import { useOpenBuild } from './use-open-build';
import './top-bar.css';

interface ChatTopBarProps {
  /** Starts a brand-new chat — the header already owns this mechanism
   *  (blank asset + `requestOpenChat` + navigate home if elsewhere). */
  onNewChat: () => void;
  /** Opens an existing chat by session id, from any page. */
  onOpenChat: (sessionId: string, title: string | null) => void;
  /** Navigates to the full Chats page (the board footer's "Open all chats"). */
  onOpenAllChats: () => void;
}

/**
 * The new chat-nav-in-the-top-bar surface (2026-08-14 new-chatbox-topbar
 * design doc): New chat button, status cluster (+ its activity board
 * dropdown), and the ask chip — the left group `app-header.tsx` renders
 * instead of the old plain New-chat button. It is unconditional now:
 * FRONT-1782 deleted the Chat design preference this used to be gated on
 * (and the classic layout it chose between), so the top bar is the only
 * chat design.
 *
 * Owns the board's open/closed state, the ⌘N shortcut, and the data
 * wiring (active chats + their statuses + pending approvals) that feeds the
 * pure `state-mapping` helpers. Opening a CHAT (where "open chat X" or
 * "start a new chat" actually goes) stays with the caller — `app-header.tsx`
 * already has the `useLocation`/`useChatOpenStore` wiring for that, and
 * reusing it here keeps there being exactly one such mechanism. FRONT-1681:
 * opening a BUILD (a Building Session row) goes through `useOpenBuild`
 * instead — that store, unlike the chat one, has no existing per-page wiring
 * in `app-header.tsx` to reuse. 2026-09-01: that hook is shared with the
 * classic design's header now, so both Build doorways navigate and hand
 * along their origin identically.
 */
export function ChatTopBar({ onNewChat, onOpenChat, onOpenAllChats }: ChatTopBarProps) {
  const [boardOpen, setBoardOpen] = useState(false);
  const openBuild = useOpenBuild();

  // FRONT-1579: the active-chats + statuses → BarChat[] → groups/counts
  // wiring now lives in `use-chat-activity.ts`, shared with the inbox chat
  // modal's `ChatActivityMenu` so the two boards can't drift apart.
  // FRONT-1681: `includeBuilds` is this surface's own opt-in — folds
  // Building Sessions into the same board, gated on `useCodingEnabled()` so
  // a deployment with coding switched off doesn't poll
  // `/api/coding/sessions` every 15s from this always-mounted header for
  // nothing. The in-modal `ChatActivityMenu` leaves it off entirely (its
  // own call site passes no options), so it stays byte-identical.
  const codingEnabled = useCodingEnabled();
  // FRONT-3040: the Pro Builder row shows for exactly the people the sidebar
  // shows its Pro Builder entry to (the same per-person flag, fallback off),
  // so nobody sees a row for a window they cannot open.
  const proBuilderEnabled = useProBuilderEnabled();
  const { barChats, groups, counts } = useChatActivity({
    includeBuilds: codingEnabled,
    includeProBuilder: proBuilderEnabled,
  });
  // FRONT-2307: hand the same count to the browser tab (app-layout.tsx) —
  // one read, so the tab and this pill can never disagree. Zero on unmount
  // so a loading/redirect screen never shows a stale "(N)".
  const setAsking = useAttentionStore((s) => s.setAsking);
  useEffect(() => {
    setAsking(counts.asking);
  }, [counts.asking, setAsking]);
  useEffect(() => () => setAsking(0), [setAsking]);
  const { data: approvals = [] } = useApprovals();

  const askingChat = useMemo(() => firstAsking(barChats), [barChats]);
  const askChip = useMemo(() => resolveAskChip(askingChat, approvals), [askingChat, approvals]);

  // Global ⌥N / Alt+N — only live while this surface is mounted (i.e. the
  // topbar design is on). Note it has NO open-window guard, unlike its ⌥B
  // twin below, and that asymmetry is deliberate: starting a new chat with a
  // chat window open hands that same window a fresh thread, which is a
  // sensible thing to do, whereas ⌥B would paint a SECOND window on top of
  // the first. Same-looking badges, different rules — don't "fix" one to
  // match the other without reading both. Matched on e.code because on macOS Option+N types
  // a dead key and e.key is not "n"; the tradeoff is that e.code is the
  // PHYSICAL key, so on a non-QWERTY layout (Dvorak/Colemak) the combo sits
  // on whatever key is at the QWERTY-N position. Cmd/Ctrl+N is
  // browser-reserved and never reaches the page — see
  // NEW_CHAT_SHORTCUT_LABEL in new-chat-button.tsx, which must stay in sync
  // with this handler.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!e.altKey || e.metaKey || e.ctrlKey || e.code !== 'KeyN') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      onNewChat();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onNewChat]);

  // 2026-09-01: ⌥B / Alt+B — the Build button's twin of the handler above,
  // same physical-key and editable-target reasoning. Registered only while
  // building is switched on for the tenant, so the combo is inert exactly
  // where the button it mirrors is absent — and, like ⌥N, only while THIS
  // surface is mounted, so the classic design has neither shortcut (which is
  // why the classic header's own Build button carries no badge).
  useEffect(() => {
    if (!codingEnabled) return;
    function onKeyDown(e: KeyboardEvent) {
      if (!e.altKey || e.metaKey || e.ctrlKey || e.code !== 'KeyB') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      // Never while a full-screen window is already open. The chat window and
      // the building window are both `role="dialog" aria-modal` overlays at
      // the same depth, and `home.tsx` mounts the building one AFTER the chat
      // one — so firing here would paint the second on top of a still-mounted
      // first, and Escape would then have two layers to unwind. FRONT-1081
      // solved the same collision for the composer's coding icon by closing
      // the chat first; that icon's handler is gone, and this listener has no
      // way to close a window it does not own, so it stands down instead. The
      // BUTTON needs no such guard: a full-screen overlay covers it.
      // Scope, precisely: Radix emits `role="dialog"` on Popover AND Dialog
      // content but `aria-modal` on NEITHER, so this catches exactly the
      // app's hand-rolled full-screen overlays — the chat window, the
      // building window, the cron form and the Pro Builder window (which
      // carries these attributes ONLY while visible) — and deliberately lets the
      // activity board's popover through. A shadcn Dialog or Sheet is NOT
      // covered: ⌥B with one of those open still opens the building window,
      // navigating home and unmounting the route it belonged to. That is a
      // lesser and different problem from the overlay stacking this exists
      // to stop, and widening the selector to bare `[role="dialog"]` would
      // kill the shortcut whenever the board is open, which is worse.
      if (document.querySelector('[role="dialog"][aria-modal]')) return;
      e.preventDefault();
      openBuild();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [codingEnabled, openBuild]);

  function openFromBoard(sessionId: string, title: string | null) {
    setBoardOpen(false);
    onOpenChat(sessionId, title);
  }

  function openBuildFromBoard(sessionId: string, _title: string | null) {
    setBoardOpen(false);
    openBuild(sessionId);
  }

  // FRONT-3040: the window is mounted once in the app shell, so this works
  // from any page (the sidebar's entry does the same).
  function openProBuilderFromBoard() {
    setBoardOpen(false);
    useProBuilderOpenStore.getState().openProBuilder();
  }

  function newFromBoard() {
    setBoardOpen(false);
    onNewChat();
  }

  function allChatsFromBoard() {
    setBoardOpen(false);
    onOpenAllChats();
  }

  return (
    <div className="eddy-topbar flex min-w-0 flex-1 items-center gap-[10px]">
      <NewChatButton onClick={onNewChat} />
      {/* 2026-09-01: gated on the same `useCodingEnabled()` the board's build
       *  rows are — a deployment with building switched off must not show a
       *  door to a window it has no sessions for. */}
      {codingEnabled && <BuildButton onClick={() => openBuild()} />}
      <Popover open={boardOpen} onOpenChange={setBoardOpen}>
        <StatusCluster counts={counts} />
        <ActivityBoard
          groups={groups}
          onOpen={openFromBoard}
          onOpenBuild={openBuildFromBoard}
          onOpenProBuilder={openProBuilderFromBoard}
          onNew={newFromBoard}
          onAllChats={allChatsFromBoard}
        />
      </Popover>
      {/* FRONT-3227: beside the cluster, not inside its popover trigger. */}
      <ReviewCount />
      {askChip && (
        <AskChip
          label={askChip.label}
          action={askChip.action}
          // FRONT-1681: firstAsking can now hand back a Building Session —
          // routed to openBuild, never onOpenChat, or this would try to open
          // a chat that doesn't exist for that session id.
          // FRONT-3040: and the Pro Builder row opens its window.
          onClick={() => {
            if (askChip.kind === 'pro-builder') openProBuilderFromBoard();
            else if (askChip.kind === 'build') openBuild(askChip.sessionId);
            else onOpenChat(askChip.sessionId, askChip.title);
          }}
        />
      )}
    </div>
  );
}
